// src/UserInput/BusinessLookupSection.tsx

import { useEffect } from 'react';

import { Input } from '@/components/Input';
import { MultipleUserInput } from '@/components/MultipleUserInput';
import {
    selectAbn,
    selectBusiness,
    selectEntityType,
    setCompanyNumber,
} from '@/context/borrower';
import { selectIsNZ } from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { Abn } from '@/UserInput/Abn';
import { AbnActiveFrom } from '@/UserInput/AbnActiveFrom';
import { Acn } from '@/UserInput/Acn';
import { BusinessLookup } from '@/UserInput/BusinessLookup';
import { GstFrom } from '@/UserInput/GstFrom';
import { NumberOfDirectors } from '@/UserInput/NumberOfDirectors';

interface Props {
    baseId: string;
    showErrors: boolean;
}

export function BusinessLookupSection({
    baseId,
    showErrors,
}: Props): JSX.Element {
    const entityType = useAppSelector(selectEntityType);
    const isNZ = useAppSelector(selectIsNZ);
    const business = useAppSelector(selectBusiness);
    const abn = useAppSelector(selectAbn);
    const dispatch = useAppDispatch();

    // Clear Company Number when ABN/NZBN is cleared
    useEffect((): void => {
        if (!isNZ) return;
        if (!abn) {
            // If no ABN/NZBN, clear Company Number
            dispatch(setCompanyNumber(''));
        }
    }, [isNZ, abn, dispatch]);

    return (
        <>
            <BusinessLookup baseId={baseId} showErrors={showErrors} />
            <MultipleUserInput>
                <Abn baseId={baseId} showErrors={false} />
                {isNZ ? (
                    <Input
                        title='Company Number'
                        placeholder='Auto-populated from NZBN'
                        showErrors={false}
                        id={`${baseId}-input-companyNumber`}
                        value={business.companyNumber ?? ''}
                        disabled={true}
                    />
                ) : entityType === 'COMPANY' ? (
                    <Acn baseId={baseId} showErrors={false} />
                ) : (
                    <div className='hidden' />
                )}
            </MultipleUserInput>

            <MultipleUserInput>
                <AbnActiveFrom baseId={baseId} showErrors={false} />
                {!isNZ && entityType === 'COMPANY' ? (
                    <GstFrom baseId={baseId} showErrors={false} />
                ) : (
                    <div className='hidden' />
                )}
            </MultipleUserInput>

            <MultipleUserInput>
                <NumberOfDirectors baseId={baseId} showErrors={showErrors} />
                <div className='hidden' />
            </MultipleUserInput>
        </>
    );
}
